import React, { useState, useRef, useEffect } from 'react'
import { X, ZoomIn, ZoomOut, RotateCw, Move } from 'lucide-react'
import { Button } from './ui/button'

const AdvancedImageViewer = ({ imageBase64, onClose, title = "Image Viewer" }) => {
  const [scale, setScale] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })
  const [windowPosition, setWindowPosition] = useState({ x: 100, y: 100 })
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const [isDraggingWindow, setIsDraggingWindow] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const imageRef = useRef(null)
  const windowRef = useRef(null)

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3))
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5))
  const handleRotate = () => setRotation(prev => (prev + 90) % 360)
  const handleReset = () => {
    setScale(1)
    setRotation(0)
    setImagePosition({ x: 0, y: 0 })
  }

  // Window dragging
  const handleWindowMouseDown = (e) => {
    if (e.target.closest('button')) return // Don't drag when clicking buttons
    setIsDraggingWindow(true)
    setDragStart({
      x: e.clientX - windowPosition.x,
      y: e.clientY - windowPosition.y
    })
  }

  const handleWindowMouseMove = (e) => {
    if (!isDraggingWindow) return
    setWindowPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }

  const handleWindowMouseUp = () => {
    setIsDraggingWindow(false)
  }

  // Image panning
  const handleImageMouseDown = (e) => {
    e.stopPropagation()
    setIsDraggingImage(true)
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    })
  }

  const handleImageMouseMove = (e) => {
    if (!isDraggingImage) return
    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }

  const handleImageMouseUp = () => {
    setIsDraggingImage(false)
  }

  useEffect(() => {
    if (isDraggingWindow) {
      document.addEventListener('mousemove', handleWindowMouseMove)
      document.addEventListener('mouseup', handleWindowMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleWindowMouseMove)
        document.removeEventListener('mouseup', handleWindowMouseUp)
      }
    }
  }, [isDraggingWindow, dragStart])

  useEffect(() => {
    if (isDraggingImage) {
      document.addEventListener('mousemove', handleImageMouseMove)
      document.addEventListener('mouseup', handleImageMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleImageMouseMove)
        document.removeEventListener('mouseup', handleImageMouseUp)
      }
    }
  }, [isDraggingImage, dragStart])

  return (
    <>
      {/* Semi-transparent overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} data-testid="image-viewer-overlay" />
      
      {/* Floating draggable window */}
      <div 
        ref={windowRef}
        className="fixed bg-white rounded-lg shadow-2xl z-50 flex flex-col"
        style={{
          left: `${windowPosition.x}px`,
          top: `${windowPosition.y}px`,
          width: '800px',
          height: '600px',
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 40px)'
        }}
        data-testid="image-viewer"
      >
        {/* Header - Draggable */}
        <div 
          className="bg-gray-900 text-white p-3 flex justify-between items-center rounded-t-lg cursor-move"
          onMouseDown={handleWindowMouseDown}
        >
          <h2 className="text-base font-semibold">{title}</h2>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              title="Zoom In"
              data-testid="zoom-in"
              className="text-white hover:bg-gray-800 h-8 w-8 p-0"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              title="Zoom Out"
              data-testid="zoom-out"
              className="text-white hover:bg-gray-800 h-8 w-8 p-0"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRotate}
              title="Rotate"
              data-testid="rotate"
              className="text-white hover:bg-gray-800 h-8 w-8 p-0"
            >
              <RotateCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              title="Reset View"
              data-testid="reset"
              className="text-white hover:bg-gray-800 h-8 w-8 p-0"
            >
              <Move className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              title="Close"
              data-testid="close-viewer"
              className="text-white hover:bg-gray-800 h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Image Container */}
        <div className="flex-1 overflow-hidden flex items-center justify-center bg-gray-100 cursor-move">
          <img
            ref={imageRef}
            src={`data:image/jpeg;base64,${imageBase64}`}
            alt="Medicine Delivery Challan"
            className="max-w-none select-none"
            style={{
              transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${scale}) rotate(${rotation}deg)`,
              transition: isDraggingImage ? 'none' : 'transform 0.1s ease-out',
              cursor: isDraggingImage ? 'grabbing' : 'grab',
              maxWidth: '100%',
              maxHeight: '100%'
            }}
            onMouseDown={handleImageMouseDown}
            draggable={false}
            data-testid="viewer-image"
          />
        </div>

        {/* Footer with zoom info */}
        <div className="bg-gray-800 text-white px-3 py-2 text-center text-xs rounded-b-lg">
          Zoom: {Math.round(scale * 100)}% | Rotation: {rotation}° | Drag header to move window, drag image to pan
        </div>
      </div>
    </>
  )
}

export default AdvancedImageViewer
